import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PoliticaContent, PoliticaContentType } from '@/hooks/usePoliticasContent';
import { RichTextEditor } from './RichTextEditor';
import { sanitizeHTML } from '@/utils/htmlSanitizer';

interface EditPerguntaFrequenteDialogProps {
  politica: PoliticaContent | null;
  open: boolean;
  onClose: () => void;
  onSave: (politica: PoliticaContent) => void;
}

const TYPE_OPTIONS: { value: PoliticaContentType; label: string }[] = [
  { value: 'Documentação', label: 'Documentação' },
  { value: 'Usuários e permissões', label: 'Usuários e permissões' },
  { value: 'Coletas', label: 'Coletas' },
  { value: 'Dados', label: 'Dados' },
  { value: 'Geral', label: 'Geral' },
];

export const EditPerguntaFrequenteDialog = ({ politica, open, onClose, onSave }: EditPerguntaFrequenteDialogProps) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState<PoliticaContentType | null>(null);

  useEffect(() => {
    if (politica) {
      setTitle(politica.title);
      setContent(politica.content.html);
      setType(politica.type);
    }
  }, [politica]);

  const handleSave = () => {
    if (!politica) return;

    const sanitizedContent = sanitizeHTML(content);

    onSave({
      ...politica,
      title,
      content: { html: sanitizedContent },
      type,
    });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar pergunta frequente</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="title">Título</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Nova pergunta frequente"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="type">Categoria</Label>
            <Select value={type || ""} onValueChange={(value) => setType(value as PoliticaContentType)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione uma categoria" />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="content">Conteúdo</Label>
            <RichTextEditor
              content={content}
              onChange={setContent}
              placeholder="Digite o conteúdo aqui..."
            />
            <p className="text-xs text-muted-foreground">
              Use a barra de ferramentas para formatar o texto. Atalhos: Ctrl+B (negrito), Ctrl+I (itálico), Ctrl+K (link)
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
